import { useEffect } from "react";
import I_IMC_Props from "../infos/I_IMC_Props";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Globals } from "../globals";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import { useTranslation } from "react-i18next";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import PreacherManagement from "./PreacherManagement";
import ChoirSongManagement from "./ChoirSongManagement";
import NotificationSettings from "./NotificationSettings";
import SermonPlanImport from "./SermonPlanImport";
import GemeindeMembers from "./GemeindeMembers";

// Gemeinde-scoped management. The Gemeinde's own accounts are under "members";
// the superadmin panel manages accounts across all Gemeinden.
type AdminTab = "preachers" | "choirsongs" | "sermonplan" | "notifications" | "members";

const TAB_LABELS: Record<AdminTab, string> = {
    preachers: "admin.preacherManagement",
    choirsongs: "admin.choirSongManagement",
    sermonplan: "admin.sermonPlanImport",
    notifications: "admin.notificationSettings",
    members: "members.title",
};

const VALID_TABS = Object.keys(TAB_LABELS) as AdminTab[];
const DEFAULT_TAB: AdminTab = "preachers";

export default function AdminPanel({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loading, canEdit } = useAuth();
    const { activeGemeindeId } = useGemeinde();

    // The active tab is driven by the URL so the shared sidebar can link to it.
    const tabParam = searchParams.get("tab") as AdminTab | null;
    const activeTab: AdminTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : DEFAULT_TAB;

    useEffect(() => {
        if (loading) return;
        // Only Gemeinde admins (and the superadmin) may manage Gemeinde data.
        if (location.pathname.startsWith(Globals.PATH_ADMIN) && !canEdit) {
            setVariant("danger");
            setMessage(t("admin.notAuthorized"));
            setShowMessage(true);
            navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST);
        }
    }, [location, navigate, setVariant, setMessage, setShowMessage, t, canEdit, loading]);

    if (loading) {
        return (
            <div className="text-center" style={{ padding: "3rem" }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (!canEdit) return null;

    // The superadmin must pick a Gemeinde (via the switcher) before managing it.
    if (!activeGemeindeId) {
        return (
            <div className="gl-content">
                <Alert variant="info">{t("gemeinde.selectFirst")}</Alert>
            </div>
        );
    }

    function renderContent() {
        switch (activeTab) {
            case "preachers":
                return <PreacherManagement setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />;
            case "choirsongs":
                return <ChoirSongManagement setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />;
            case "sermonplan":
                return <SermonPlanImport setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />;
            case "notifications":
                return <NotificationSettings setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />;
            case "members":
                return <GemeindeMembers setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />;
        }
    }

    return (
        <div className="gl-content">
            <div className="d-flex align-items-end justify-content-between mb-4">
                <div>
                    <div className="gl-meta mb-1">Admin · {t(TAB_LABELS[activeTab])}</div>
                    <h1 className="mb-0">{t(TAB_LABELS[activeTab])}</h1>
                </div>
            </div>

            {renderContent()}
        </div>
    );
}
